// Calendar Module Tour
// Automated booking and appointment management

export const calendarTour = {
  id: 'calendar',
  name: 'Calendar Module Tour',
  displayName: 'Automate Your Appointment Booking',
  description: 'Let clients book time with you automatically—no more phone tag',
  estimatedDuration: '4 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Calendars
    {
      element: 'nav #sb_calendars',
      popover: {
        title: '📅 Welcome to Calendars',
        description: `
          Say goodbye to phone tag! The Calendar system lets clients book appointments 
          with you automatically based on your real availability. Set it once, and 
          it runs 24/7—accepting bookings, sending confirmations, and reminders.
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: Calendar Types
    {
      popover: {
        title: '📆 What Are Calendars?',
        description: `
          Calendars are booking pages for different types of appointments. You might create:
          • **Discovery Call** (30 minutes)
          • **Strategy Session** (60 minutes)
          • **Follow-up Meeting** (15 minutes)
          
          Each calendar has its own duration, availability rules, and booking page. 
          Clients choose which type of appointment they want.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Calendar Settings Button
    {
      element: 'button:contains("Calendar Settings"), [href*="calendar-settings"]',
      popover: {
        title: '⚙️ Calendar Settings',
        description: `
          Start here to create your first calendar. You'll define:
          • Calendar name (e.g., "Discovery Call")
          • Duration (15, 30, 60 minutes, etc.)
          • Buffer time between appointments
          • Which team member handles these bookings
        `,
        side: 'left',
        align: 'start',
        proTip: 'Create multiple calendars for different services or team members. Each gets its own booking link!'
      }
    },

    // Step 4: Calendar View
    {
      element: '[class*="calendar-view"], [class*="schedule"]',
      popover: {
        title: '🗓️ Your Schedule at a Glance',
        description: `
          This calendar view shows all your upcoming appointments. You can:
          • Click any time slot to manually book an appointment
          • Drag appointments to reschedule them
          • Color-code by appointment type
          • View by day, week, or month
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 5: Appointment List View
    {
      element: 'button:contains("Appointment List"), [class*="list-view"]',
      popover: {
        title: '📋 List All Appointments',
        description: `
          Prefer a list? Switch to Appointment List View to see all bookings in 
          a sortable table. Filter by date range, status (confirmed, cancelled, 
          completed), or calendar type. Great for reporting and bulk management.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 6: Availability Settings
    {
      popover: {
        title: '⏰ Set Your Availability',
        description: `
          Under Calendar Settings, you'll configure when clients can book:
          • **Business hours**: M-F, 9am-5pm (for example)
          • **Specific days off**: Holidays, vacations
          • **Time zone**: Automatically converts for clients
          • **Minimum notice**: Require 24 hours advance booking
          • **Date range**: Only allow bookings 30 days out
          
          This prevents bookings at inconvenient times or when you're unavailable.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 7: Booking Page (Widget)
    {
      popover: {
        title: '🔗 Share Your Booking Page',
        description: `
          Each calendar gets a unique booking page URL. Share this link with clients:
          • In your email signature
          • On your website
          • In text messages
          • On social media
          
          Clients click the link, see your available times, and book instantly. 
          No back-and-forth needed!
        `,
        side: 'over',
        align: 'center',
        proTip: 'Embed the booking widget directly on your website for seamless booking without leaving your site!'
      }
    },

    // Step 8: Calendar Integrations
    {
      popover: {
        title: '🔄 Sync with Google/Outlook',
        description: `
          Connect your Google Calendar or Outlook to:
          • **Check availability**: System won't book when you're busy
          • **Two-way sync**: Appointments appear in both calendars
          • **Prevent double-booking**: Automatically blocks time
          
          Go to Settings > Integrations > Calendar to connect.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 9: Notifications & Reminders
    {
      popover: {
        title: '🔔 Automatic Notifications',
        description: `
          The system automatically sends:
          • **Confirmation email/SMS** when booked
          • **Reminders** (1 day before, 1 hour before—you choose)
          • **Follow-up** messages after the appointment
          • **Cancellation/reschedule** confirmations
          
          All automatic. You configure it once in Calendar Settings.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 10: Forms & Custom Questions
    {
      popover: {
        title: '📝 Collect Information at Booking',
        description: `
          Add custom questions to your booking form:
          • "What's your biggest challenge right now?"
          • "What's your budget range?"
          • "How did you hear about us?"
          
          Answers are saved to the contact record. You'll see them before the 
          meeting, so you can prepare and personalize the conversation.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 11: Group Calendars (Events)
    {
      popover: {
        title: '👥 Group Events & Webinars',
        description: `
          Beyond one-on-one appointments, you can create group event calendars:
          • Webinars with limited seats
          • Workshops or training sessions
          • Open houses or tours
          
          Set a max capacity, and the system handles registration limits automatically.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 12: Round Robin (Team Booking)
    {
      popover: {
        title: '🔄 Round Robin for Teams',
        description: `
          Have multiple team members who handle appointments? Enable round robin 
          distribution. The system automatically assigns bookings to the next 
          available team member, balancing workload fairly.
          
          Perfect for sales teams, support, or service businesses.
        `,
        side: 'over',
        align: 'center',
        proTip: 'Set priority levels for team members so VIPs always book with your senior staff!'
      }
    },

    // Step 13: Completion
    {
      popover: {
        title: '🎉 You\'re a Booking Master!',
        description: `
          Amazing! You now know how to:
          ✅ Create calendars for different appointment types
          ✅ Set your availability rules
          ✅ Share booking pages with clients
          ✅ Sync with Google/Outlook
          ✅ Send automatic reminders
          ✅ Collect information via booking forms
          
          **Next Steps:**
          • Create your first calendar (go to Calendar Settings)
          • Connect your Google/Outlook calendar
          • Add the booking link to your email signature
          • Explore Automation to trigger workflows after bookings
          
          Never play phone tag again! 📞❌
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Let\'s Book! →'
      }
    }
  ]
};
